import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, Check, ShoppingBag } from "lucide-react";
import { FadeUp } from "../components/Reveal";
import { ProductCard } from "../components/ShopUI";
import { fetchShopProduct, fetchShopProducts } from "../lib/api";
import { money, useShop } from "../context/ShopContext";

const ScentPicker = ({ scents, required, picks, setPicks }) => {
  const chosen = Object.values(picks).reduce((a, b) => a + b, 0);
  const bump = (name, delta) => {
    const next = Math.max(0, (picks[name] || 0) + delta);
    if (delta > 0 && chosen >= required) return;
    setPicks({ ...picks, [name]: next });
  };

  return (
    <div className="mt-10 border-t border-cream-border pt-8" data-testid="scent-picker">
      <div className="flex items-baseline justify-between">
        <p className="text-[0.65rem] uppercase tracking-[0.18em] text-ink-muted">
          {required === 1 ? "Choose your fragrance" : `Choose your ${required} fragrances`}
        </p>
        <span className={`text-xs font-semibold ${chosen === required ? "text-gold" : "text-ink-muted"}`} data-testid="scent-counter">
          {chosen} / {required} selected
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
        {scents.map((s) => {
          const qty = picks[s.name] || 0;
          return (
            <div key={s.id}
              className={`flex items-center justify-between gap-3 border px-4 py-3 transition-colors ${qty ? "border-gold bg-[#fbf8f1]" : "border-cream-border bg-[#fbf8f1]"}`}
              data-testid={`scent-option-${s.slug}`}>
              <div className="min-w-0">
                <p className="text-sm text-ink truncate">{s.name}</p>
                <p className="text-[0.65rem] text-ink-muted truncate">{s.notes || s.subtitle}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => bump(s.name, -1)} disabled={!qty}
                  className="w-7 h-7 grid place-items-center border border-cream-border text-ink disabled:opacity-30 hover:border-gold transition-colors"
                  data-testid={`scent-minus-${s.slug}`} aria-label={`Remove ${s.name}`}><Minus size={12} /></button>
                <span className="w-4 text-center text-sm text-ink" data-testid={`scent-qty-${s.slug}`}>{qty}</span>
                <button onClick={() => bump(s.name, 1)} disabled={chosen >= required}
                  className="w-7 h-7 grid place-items-center border border-cream-border text-ink disabled:opacity-30 hover:border-gold transition-colors"
                  data-testid={`scent-plus-${s.slug}`} aria-label={`Add ${s.name}`}><Plus size={12} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, cart } = useShop();
  const [product, setProduct] = useState(null);
  const [all, setAll] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [picks, setPicks] = useState({});

  useEffect(() => {
    setProduct(null); setNotFound(false); setPicks({}); setQty(1); setActive(0);
    fetchShopProduct(slug).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    fetchShopProducts().then((r) => setAll(r.data)).catch(() => {});
  }, [slug]);

  const scents = useMemo(() => all.filter((p) => p.category === "scent"), [all]);
  const related = useMemo(
    () => all.filter((p) => p.slug !== slug).slice(0, 3),
    [all, slug]
  );

  if (notFound) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-cream px-6" data-testid="product-not-found">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-ink">We couldn't find that one.</h1>
          <Link to="/shop" className="inline-flex items-center gap-2 mt-6 text-gold text-xs uppercase tracking-[0.2em]">
            <ArrowLeft size={14} /> Back to the shop
          </Link>
        </div>
      </div>
    );
  }

  if (!product) return <div className="min-h-[70vh] grid place-items-center bg-cream text-ink-muted">Loading…</div>;

  const required = product.scent_choice_count || 0;
  const chosen = Object.values(picks).reduce((a, b) => a + b, 0);
  const gallery = product.images?.length ? product.images : [product.image];

  const add = (goToCart) => {
    if (required && chosen !== required) {
      toast.error(`Please choose ${required} fragrance${required > 1 ? "s" : ""} to continue.`);
      return;
    }
    if (product.billing_interval && cart.some((l) => !l.billing_interval)) {
      toast.error("Refill plans are checked out on their own. Please order your other items first.");
      return;
    }
    if (!product.billing_interval && cart.some((l) => l.billing_interval)) {
      toast.error("You have a refill plan in your bag — please check that out on its own first.");
      return;
    }
    const list = Object.entries(picks).flatMap(([name, n]) => Array.from({ length: n }, () => name));
    addToCart(product, qty, list);
    toast.success(`${product.name} added to your bag.`);
    if (goToCart) navigate("/cart");
  };

  return (
    <div className="bg-cream" data-testid="product-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-20">
        <Link to="/shop" className="inline-flex items-center gap-2 text-ink-muted text-xs uppercase tracking-[0.18em] hover:text-gold transition-colors" data-testid="back-to-shop">
          <ArrowLeft size={14} /> Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mt-10">
          <div>
            <div className="relative aspect-square overflow-hidden bg-[#f4eee2] border border-cream-border">
              <img src={gallery[active]} alt={product.name} className="absolute inset-0 w-full h-full object-cover" data-testid="product-main-image" />
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mt-3">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setActive(i)}
                    className={`w-20 h-20 overflow-hidden border transition-colors ${i === active ? "border-gold" : "border-cream-border hover:border-[#bba77f]"}`}
                    data-testid={`product-thumb-${i}`}>
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="eyebrow mb-5">{product.category === "scent" ? "Fragrance Oil" : product.category === "subscription" ? "Refill Plan" : product.category}</p>
            <h1 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-ink" data-testid="product-name">{product.name}</h1>
            {product.subtitle && <p className="text-ink-muted mt-4 leading-relaxed">{product.subtitle}</p>}
            <p className="font-serif text-3xl text-ink mt-8 flex items-baseline gap-3" data-testid="product-detail-price">
              <span>{money(product.price)}{product.billing_interval ? <span className="text-xl text-ink-muted"> / {product.billing_interval}</span> : null}</span>
              {product.compare_price > product.price && (
                <span className="text-base text-ink-muted line-through font-sans">{money(product.compare_price)}</span>
              )}
            </p>
            <p className="text-xs text-ink-muted mt-2">
              {product.billing_interval
                ? "Includes GST and delivery. Billed monthly, cancel any time from your account."
                : "Includes GST. Flat-rate delivery added at checkout."}
            </p>

            {product.description && (
              <p className="text-ink-muted leading-relaxed mt-8 border-t border-cream-border pt-8">{product.description}</p>
            )}

            {required > 0 && scents.length > 0 && (
              <ScentPicker scents={scents} required={required} picks={picks} setPicks={setPicks} />
            )}

            <div className="flex items-center gap-4 mt-10">
              <div className="flex items-center border border-cream-border">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-12 grid place-items-center text-ink hover:text-gold transition-colors" data-testid="qty-minus"><Minus size={14} /></button>
                <span className="w-10 text-center text-ink" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(20, q + 1))} className="w-11 h-12 grid place-items-center text-ink hover:text-gold transition-colors" data-testid="qty-plus"><Plus size={14} /></button>
              </div>
              <button onClick={() => add(false)} data-testid="add-to-cart"
                className="group flex-1 inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#221d17] text-cream text-xs font-semibold uppercase tracking-[0.2em] hover:bg-black transition-colors duration-300">
                <ShoppingBag size={15} /> Add to bag
              </button>
            </div>
            <button onClick={() => add(true)} data-testid="buy-now"
              className="w-full mt-3 inline-flex items-center justify-center gap-3 px-8 py-4 border border-[#221d17] text-[#221d17] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#221d17] hover:text-cream transition-colors duration-300">
              <Check size={15} /> Add & checkout
            </button>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="bg-cream-alt border-t border-cream-border" data-testid="related-products">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
            <FadeUp><h2 className="font-serif text-2xl md:text-3xl text-ink mb-10">You might also like</h2></FadeUp>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {related.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
